// Canonical single source of truth for the three active venues (NAP, content, SEO, reservation, schema @id).
// Consumed by: /locales, /locales/:slug, footer, per-page JSON-LD, and the parity tests that keep
// public/index.html (global JSON-LD) and public/sitemap.xml aligned with this file.
// NOTE: public/index.html and public/sitemap.xml are static files and CANNOT read this at runtime;
// they are updated explicitly and guarded by locations.parity.test.js.
import { ASSETS } from "./assets";

export const SITE_URL = "https://gruporitual.com";

const SERVES_CUISINE = ["Café de especialidad", "Brunch", "Mediterránea"];
const PRICE_RANGE = "€€";
const HOURS_STD = {
  es: "Lun, Mié-Dom: 9:00-23:00 · Martes: Cerrado",
  en: "Mon, Wed-Sun: 9:00-23:00 · Tuesday: Closed",
  va: "Dl, Dc-Dg: 9:00-23:00 · Dimarts: Tancat",
};

export const LOCATIONS = [
  {
    slug: "valterna",
    name: "Ritual Café Valterna",
    shortName: "Valterna",
    area: { es: "Paterna, Valencia", en: "Paterna, Valencia", va: "Paterna, València" },
    desc: {
      es: "Restaurante, cafetería y brunch en Paterna, ubicado en Valterna. Desayunos, almuerzos, comidas, cenas y café de especialidad en un espacio pensado para disfrutar sin prisas.\nNuestro primer local. Aquí comenzó la historia de Grupo Ritual y aprendimos que el café conecta personas.",
      en: "Our first venue. Where the story of Grupo Ritual began and we learned that coffee connects people.",
      va: "El nostre primer local. On va començar la història del Grup Ritual i vam aprendre que el café connecta persones.",
    },
    address: { streetAddress: "Carrer Ravanell, 1", postalCode: "46980", addressLocality: "Paterna", addressRegion: "Valencia", addressCountry: "ES" },
    addressDisplay: "Carrer Ravanell, 1, 46980 Paterna, Valencia",
    phoneDisplay: "+34 960 41 98 55",
    phoneTel: "+34960419855",
    hours: HOURS_STD,
    // No confirmed real photo yet — Valterna renders as a premium text block (no image).
    image: null,
    servesCuisine: SERVES_CUISINE,
    priceRange: PRICE_RANGE,
    reservation: { mode: "covermanager", coverManagerSlug: "restaurante-ritual-cafe" },
    geo: null,
    openingHoursSpecification: null,
    seoTitle: {
      es: "Ritual Café Valterna | Restaurante y brunch en Paterna",
      en: "Ritual Café Valterna | Restaurant and brunch in Paterna",
      va: "Ritual Café Valterna | Restaurant i brunch a Paterna",
    },
  },
  {
    slug: "lliria",
    name: "Ritual Café Llíria",
    shortName: "Llíria",
    area: { es: "Llíria, Valencia", en: "Llíria, Valencia", va: "Llíria, València" },
    desc: {
      es: "Nuestro segundo hogar, donde el amor por el café crece cada día en el corazón de Llíria.",
      en: "Our second home, where the love for coffee grows every day in the heart of Llíria.",
      va: "La nostra segona casa, on l'amor pel café creix cada dia al cor de Llíria.",
    },
    address: { streetAddress: "Calle Llibertat, 9", postalCode: "46160", addressLocality: "Llíria", addressRegion: "Valencia", addressCountry: "ES" },
    addressDisplay: "Calle Llibertat, 9, 46160 Llíria, Valencia",
    phoneDisplay: "+34 960 52 46 47",
    phoneTel: "+34960524647",
    hours: HOURS_STD,
    image: ASSETS.lliria,
    servesCuisine: SERVES_CUISINE,
    priceRange: PRICE_RANGE,
    reservation: { mode: "own", coverManagerSlug: null },
    geo: null,
    openingHoursSpecification: null,
    seoTitle: {
      es: "Ritual Café Llíria | Restaurante y brunch en Llíria",
      en: "Ritual Café Llíria | Restaurant and brunch in Llíria",
      va: "Ritual Café Llíria | Restaurant i brunch a Llíria",
    },
  },
  {
    slug: "rocafort",
    name: "Ritual Café Rocafort",
    shortName: "Rocafort",
    area: { es: "Rocafort, Valencia", en: "Rocafort, Valencia", va: "Rocafort, València" },
    desc: {
      es: "El más reciente de nuestros espacios. Un rincón donde vivir el café con alma y autenticidad.",
      en: "The newest of our spaces. A corner to experience coffee with soul and authenticity.",
      va: "El més recent dels nostres espais. Un racó on viure el café amb ànima i autenticitat.",
    },
    address: { streetAddress: "Calle Burjassot, 1", postalCode: "46111", addressLocality: "Rocafort", addressRegion: "Valencia", addressCountry: "ES" },
    addressDisplay: "Calle Burjassot, 1, 46111 Rocafort, Valencia",
    phoneDisplay: "+34 961 07 69 25",
    phoneTel: "+34961076925",
    hours: HOURS_STD,
    image: ASSETS.rocafortInterior,
    servesCuisine: SERVES_CUISINE,
    priceRange: PRICE_RANGE,
    reservation: { mode: "covermanager", coverManagerSlug: "restaurante-ritual-cafe-rocafort" },
    geo: null,
    openingHoursSpecification: null,
    seoTitle: {
      es: "Ritual Café Rocafort | Restaurante y brunch en Rocafort",
      en: "Ritual Café Rocafort | Restaurant and brunch in Rocafort",
      va: "Ritual Café Rocafort | Restaurant i brunch a Rocafort",
    },
  },
];

export const getLocation = (slug) => LOCATIONS.find((l) => l.slug === slug) || null;
export const locationUrl = (slug) => `${SITE_URL}/locales/${slug}`;
export const locationJsonLdId = (slug) => `${SITE_URL}/locales/${slug}#restaurant`;

// Per-venue Schema.org Restaurant. Optional fields (image/geo/openingHours) are only emitted
// when confirmed data exists — never invented.
export function buildLocationJsonLd(loc) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": locationJsonLdId(loc.slug),
    name: loc.name,
    url: locationUrl(loc.slug),
    servesCuisine: loc.servesCuisine,
    priceRange: loc.priceRange,
    acceptsReservations: "True",
    address: { "@type": "PostalAddress", ...loc.address },
  };
  if (loc.phoneTel) data.telephone = loc.phoneTel;
  if (loc.image) data.image = loc.image;
  if (loc.geo) data.geo = { "@type": "GeoCoordinates", ...loc.geo };
  if (loc.openingHoursSpecification) data.openingHoursSpecification = loc.openingHoursSpecification;
  return data;
}
